import type { ReactNode } from "react";
import { DocumentDesk, type DeskDocument } from "../../documents/desk.js";
import type { DeskPlace } from "../../documents/landing.js";
import { PanelHeading } from "./chrome.js";

/**
 * THE DESK PANEL — the lower right of the Storyteller composition.
 *
 * A surface with papers on it, and nothing else. The papers are arranged by
 * the learner and stay where they are put; picking one up carries it off the
 * desk and opens it as a window over the conversation or the scene
 * (`paper-window.tsx`), which is where a document is read against the thing it
 * is evidence for.
 *
 * The panel therefore has no reader, no page control and no switcher of its
 * own: a heading that says how many papers there are, the desk itself, and one
 * line at the foot that says what the desk is for.
 */
export function DeskPanel({
  sessionId,
  cards,
  openIds,
  places,
  raisedIds,
  seenIds,
  notesInHand,
  onOpen,
  onOpenAt,
  onPlace,
  onSeen,
  onOpenNotes,
}: {
  readonly sessionId: string;
  readonly cards: readonly DeskDocument[] | null;
  readonly openIds: readonly string[];
  readonly places: Readonly<Record<string, DeskPlace>>;
  readonly raisedIds: readonly string[];
  readonly seenIds: readonly string[];
  readonly notesInHand: boolean;
  readonly onOpen: (doc: DeskDocument) => void;
  readonly onOpenAt: (doc: DeskDocument, clientX: number, clientY: number) => void;
  readonly onPlace: (id: string, place: DeskPlace) => void;
  readonly onSeen: (ids: readonly string[]) => void;
  readonly onOpenNotes: () => void;
}): ReactNode {
  const count = cards?.length ?? 0;
  return (
    <section className="pcs-panel pcs-desk" data-region="desk" aria-label="Your desk">
      <PanelHeading label="On your desk">{count === 1 ? "1 document" : `${String(count)} documents`}</PanelHeading>

      <div className="pcs-page pcs-desk-surface">
        <DocumentDesk
          sessionId={sessionId}
          cards={cards}
          openIds={openIds}
          places={places}
          raisedIds={raisedIds}
          seenIds={seenIds}
          onOpen={onOpen}
          onOpenAt={onOpenAt}
          onPlace={onPlace}
          onSeen={onSeen}
        />
      </div>

      <div className="pcs-desk-foot">
        <button type="button" onClick={onOpenNotes} disabled={notesInHand}>
          in your own notes.
        </button>
        <span className="pcs-quiet pcs-truncate">
          {count === 0 ? "Nothing here yet." : "Pick one up to read it."}
        </span>
      </div>
    </section>
  );
}
